import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "./index";
import { useToast } from "@/components/ui/use-toast";
import type {
  HotTakesConfig,
  HotTakesResult,
} from "@somethings-ui/core/hot-takes";

// ─── Types ───

export type ActivityType = "hot_takes" | "connect_moment";

export type ActivityStatus = "sent" | "in_progress" | "completed" | "declined";

export interface ActivityDefinition {
  label: string;
  description: string;
  allowDecline: boolean;
  renderMode: "native" | "webview";
}

export interface ConnectActivityMentorAnswer {
  question_id: string;
  library_answer_id: string;
  audio_s3_key: string | null;
  duration_ms: number | null;
  transcript: string | null;
}

export interface ConnectActivityConfig {
  cardset_id: string;
  mentor_answers: [
    ConnectActivityMentorAnswer,
    ConnectActivityMentorAnswer,
    ConnectActivityMentorAnswer,
  ];
}

export interface ConnectActivityTeenVoiceResponse {
  mode: "voice";
  audio_s3_key: string;
  duration_ms?: number;
  transcript?: string | null;
}

export interface ConnectActivityTeenTextResponse {
  mode: "text";
  content: string;
}

export type ConnectActivityTeenResponse =
  | ConnectActivityTeenVoiceResponse
  | ConnectActivityTeenTextResponse;

export interface ConnectActivityLevelResult {
  teen_response: ConnectActivityTeenResponse | null;
  revealed_at: string | null;
  advanced_at: string | null;
}

export interface ConnectActivityResult {
  levels: [
    ConnectActivityLevelResult | null,
    ConnectActivityLevelResult | null,
    ConnectActivityLevelResult | null,
  ];
}

interface BaseActivity {
  id: string;
  status: ActivityStatus;
  isExpired: boolean;
  expiresAt: string | null;
  openedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  mentorshipId: string;
  activityDefinition: ActivityDefinition | null;
}

export interface HotTakesActivity extends BaseActivity {
  activityType: "hot_takes";
  config: HotTakesConfig;
  result: HotTakesResult | null;
}

export interface ConnectActivity extends BaseActivity {
  activityType: "connect_moment";
  config: ConnectActivityConfig;
  result: ConnectActivityResult | null;
}

export type Activity = HotTakesActivity | ConnectActivity;

export const isHotTakesActivity = (
  activity: Activity | undefined,
): activity is HotTakesActivity => activity?.activityType === "hot_takes";

export const isConnectActivity = (
  activity: Activity | undefined,
): activity is ConnectActivity => activity?.activityType === "connect_moment";

export interface AvailableActivity {
  type: ActivityType;
  label: string;
  description: string;
  enabled: boolean;
  reason: string | null;
  isTeenEligible: boolean;
}

export interface AvailabilityResponse {
  activities: AvailableActivity[];
  weeklyUsed: number;
  weeklyLimit: number;
  sentToday: boolean;
}

interface SendActivityResponse {
  entity: {
    id: string;
    entityType: string;
    activity: {
      activity_type: ActivityType;
      status: ActivityStatus;
    };
  };
}

// ─── Query Keys ───

export const ACTIVITY_QUERY_KEY = "activity";
export const ACTIVITY_AVAILABILITY_KEY = "activity-availability";

// ─── Hooks ───

export const useActivity = (entityId: string) => {
  return useQuery<Activity>({
    queryKey: [ACTIVITY_QUERY_KEY, entityId],
    queryFn: async () => {
      const response = await fetchApi<Activity>({
        url: `/v2/activities/${entityId}`,
        method: "GET",
      });
      return response.data;
    },
    enabled: !!entityId,
    retry: false,
  });
};

export const useConnectActivity = (entityId: string) => {
  const query = useActivity(entityId);
  return {
    ...query,
    data: isConnectActivity(query.data) ? query.data : undefined,
  };
};

export const useAvailableActivities = (mentorshipId: string) => {
  return useQuery<AvailabilityResponse>({
    queryKey: [ACTIVITY_AVAILABILITY_KEY, mentorshipId],
    queryFn: async () => {
      const response = await fetchApi<AvailabilityResponse>({
        url: `/v2/activities/available/${mentorshipId}`,
        method: "GET",
      });
      return response.data;
    },
    enabled: !!mentorshipId,
  });
};

interface SendActivityVariables {
  mentorshipId: string;
  activityType: ActivityType;
  config?: HotTakesConfig | Record<string, unknown>;
}

export const useSendActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<SendActivityResponse, Error, SendActivityVariables>({
    mutationKey: ["activity-send"],
    mutationFn: async ({ mentorshipId, activityType, config }) => {
      const response = await fetchApi<SendActivityResponse>({
        url: "/v2/activities/send",
        method: "POST",
        data: {
          mentorshipId,
          activityType,
          ...(config ? { config } : {}),
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Activity sent",
        description: "The activity has been sent to the teen.",
      });
      queryClient.invalidateQueries({
        queryKey: [ACTIVITY_AVAILABILITY_KEY, variables.mentorshipId],
      });
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      const message =
        error?.response?.data?.error || "Failed to send activity";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};
